import { ref, onMounted, onBeforeUnmount } from 'vue'

const APP_VERSION = '0.1.0'

interface UpdateInfo {
  available: boolean
  version?: string
  notes?: string
  date?: string
  currentVersion: string
}

export const useVersionChecker = (appType: 'pos' | 'kds' | 'tablet' | 'kiosk' = 'pos') => {
  const config = useRuntimeConfig()
  const userStore = useUserStore()

  const updateAvailable = ref(false)
  const latestVersion = ref('')
  const currentVersion = ref(APP_VERSION)
  const releaseNotes = ref('')
  const isCheckingVersion = ref(false)
  const showUpdateDialog = ref(false)
  const isDownloading = ref(false)
  const downloadProgress = ref(0)
  const isInstalling = ref(false)
  const updateError = ref<string | null>(null)

  let checkInterval: NodeJS.Timeout | null = null
  let tauriUpdate: any = null

  const isTauri = (): boolean => {
    return typeof window !== 'undefined' && '__TAURI__' in window
  }

  const checkForUpdates = async (): Promise<UpdateInfo> => {
    if (isCheckingVersion.value) {
      return { available: false, currentVersion: currentVersion.value }
    }

    isCheckingVersion.value = true
    updateError.value = null

    try {
      if (isTauri()) {
        const { check } = await import('@tauri-apps/plugin-updater')
        const update = await check()

        if (update) {
          tauriUpdate = update
          latestVersion.value = update.version
          releaseNotes.value = update.body || ''
          updateAvailable.value = true
          showUpdateDialog.value = true

          console.log('[VersionChecker] Update available:', {
            current: currentVersion.value,
            latest: update.version,
            notes: update.body,
          })

          return {
            available: true,
            version: update.version,
            notes: update.body || undefined,
            date: update.date || undefined,
            currentVersion: currentVersion.value,
          }
        } else {
          console.log('[VersionChecker] No update available')
          return { available: false, currentVersion: currentVersion.value }
        }
      } else {
        const response = await fetch(
          `${config.public.dripApiServer}/api/releases/${appType}/check?platform=windows-x86_64&current_version=${currentVersion.value}`,
          {
            headers: {
              'Cache-Control': 'no-cache',
            },
          }
        )

        if (!response.ok) {
          return { available: false, currentVersion: currentVersion.value }
        }

        const data = await response.json()

        if (data.update_available === false) {
          return { available: false, currentVersion: currentVersion.value }
        }

        latestVersion.value = data.version
        releaseNotes.value = data.notes || ''
        updateAvailable.value = true
        showUpdateDialog.value = true

        return {
          available: true,
          version: data.version,
          notes: data.notes,
          date: data.pub_date,
          currentVersion: currentVersion.value,
        }
      }
    } catch (error) {
      console.error('[VersionChecker] Error checking for updates:', error)
      updateError.value = error instanceof Error ? error.message : 'Failed to check for updates'
      return { available: false, currentVersion: currentVersion.value }
    } finally {
      isCheckingVersion.value = false
    }
  }

  const downloadAndInstall = async (): Promise<void> => {
    if (!isTauri() || !tauriUpdate) {
      console.log('[VersionChecker] Cannot download update - not in Tauri or no update available')
      return
    }

    isDownloading.value = true
    downloadProgress.value = 0
    updateError.value = null

    try {
      let downloaded = 0
      let contentLength = 0

      await tauriUpdate.downloadAndInstall((event: any) => {
        switch (event.event) {
          case 'Started':
            contentLength = event.data.contentLength || 0
            console.log(`[VersionChecker] Download started, size: ${contentLength} bytes`)
            break
          case 'Progress':
            downloaded += event.data.chunkLength
            if (contentLength > 0) {
              downloadProgress.value = Math.round((downloaded / contentLength) * 100)
            }
            break
          case 'Finished':
            console.log('[VersionChecker] Download finished')
            isDownloading.value = false
            isInstalling.value = true
            break
        }
      })

      console.log('[VersionChecker] Update installed, relaunching...')
      const { relaunch } = await import('@tauri-apps/plugin-process')
      await relaunch()
    } catch (error) {
      console.error('[VersionChecker] Error downloading/installing update:', error)
      updateError.value = error instanceof Error ? error.message : 'Failed to install update'
      isDownloading.value = false
      isInstalling.value = false
    }
  }

  const dismissUpdate = (): void => {
    showUpdateDialog.value = false
  }

  const sendHeartbeat = async (): Promise<void> => {
    try {
      const deviceId = localStorage.getItem('deviceId') || generateDeviceId()

      await fetch(`${config.public.dripApiServer}/api/version/heartbeat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appType,
          currentVersion: currentVersion.value,
          deviceId,
          businessId: userStore.activeBusinessId,
          platform: isTauri() ? 'tauri' : 'web',
        }),
      })
    } catch (error) {
      console.error('[VersionChecker] Heartbeat error:', error)
    }
  }

  const generateDeviceId = (): string => {
    const id = `${appType}-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
    localStorage.setItem('deviceId', id)
    return id
  }

  const startVersionCheck = (): void => {
    checkForUpdates()

    checkInterval = setInterval(
      () => {
        checkForUpdates()
      },
      2 * 60 * 60 * 1000
    )
  }

  const stopVersionCheck = (): void => {
    if (checkInterval) {
      clearInterval(checkInterval)
      checkInterval = null
    }
  }

  onMounted(() => {
    if (isTauri()) {
      import('@tauri-apps/api/app').then(({ getVersion }) => {
        getVersion().then((version) => {
          currentVersion.value = version
          console.log('[VersionChecker] Current Tauri app version:', version)
        })
      })
    }

    startVersionCheck()
    sendHeartbeat()
  })

  onBeforeUnmount(() => {
    stopVersionCheck()
  })

  return {
    updateAvailable,
    latestVersion,
    currentVersion,
    releaseNotes,
    showUpdateDialog,
    isCheckingVersion,
    isDownloading,
    downloadProgress,
    isInstalling,
    updateError,
    checkForUpdates,
    downloadAndInstall,
    dismissUpdate,
    startVersionCheck,
    stopVersionCheck,
    isTauri,
  }
}
